import { Copy, Eye, Lock, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useActions, useOpenSecret, useProjects, useRerequestSecret, useRevokeSecret, useSecrets, useSendSecret, useWorkspace } from '@/api';
import type { Secret } from '@/api/types';
import { ConfirmButton } from '@/components/ConfirmDialog';
import { QueryPending } from '@/components/QueryState';
import { Avatar, Empty, PageHeader, Status, Tabs } from '@/components/ui';
import { tabPanel } from '@/lib/tabs';
import { SECRET_NOTICE } from '@/lib/access';
import { ApiError } from '@/api/http';
import { errorMessage } from '@/lib/errors';
import { formatWhen } from '@/lib/format';
import { secretStatus } from '@/lib/status';

const ttl = [
  { hours: 24, label: '1 день' },
  { hours: 72, label: '3 дня' },
  { hours: 168, label: '7 дней' },
];

function SendForm() {
  const [params] = useSearchParams();
  const { data: ws } = useWorkspace();
  const { data: projects } = useProjects();
  const send = useSendSecret();
  const studio = ws?.people.filter((p) => p.side === 'studio') ?? [];
  const [toId, setToId] = useState(params.get('to') ?? '');
  const [projectId, setProjectId] = useState(params.get('project') ?? '');
  const [title, setTitle] = useState(params.get('title') ?? '');
  // The studio's request this access answers; the server closes it when the access is sent.
  const [onboardingId, setOnboardingId] = useState(params.get('onboarding') ?? '');
  const [value, setValue] = useState('');
  const [hours, setHours] = useState(72);
  const [sent, setSent] = useState<Secret | null>(null);

  // «Передать» on a request fills the form in place.
  const query = params.toString();
  useEffect(() => {
    setProjectId(params.get('project') ?? '');
    setTitle(params.get('title') ?? '');
    setOnboardingId(params.get('onboarding') ?? '');
    if (params.get('to')) setToId(params.get('to')!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const recipient = toId || studio[0]?.id || '';
  const canSend = recipient && title.trim() && value.trim() && !send.isPending;

  return (
    <form
      className="panel p-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSend) return;
        send.mutate(
          { title: title.trim(), toId: recipient, projectId: projectId || undefined, onboardingId: onboardingId || undefined, value, ttlHours: hours },
          {
            onSuccess: (s) => {
              setSent(s);
              setValue('');
              setTitle('');
              setOnboardingId('');
            },
          },
        );
      }}
    >
      <h2 className="font-semibold">Передать доступ</h2>
      <p className="mt-1 text-sm text-muted">Откроет только выбранный человек и только один раз. {SECRET_NOTICE}</p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Кому</span>
          <select className="field" value={recipient} onChange={(e) => setToId(e.target.value)}>
            {studio.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Проект</span>
          <select className="field" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Без проекта</option>
            {projects?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm sm:col-span-2">
          <span className="mb-1.5 block text-muted">Что это</span>
          <input className="field" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Например, админка amoCRM" />
        </label>
        <label className="block text-sm sm:col-span-2">
          <span className="mb-1.5 block text-muted">Логин, пароль, ключ</span>
          <textarea
            className="field min-h-24 font-mono text-sm"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            placeholder="Шифруется в браузере перед отправкой"
          />
        </label>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted">Действует</span>
          <div className="flex rounded bg-sunken p-0.5" role="radiogroup" aria-label="Срок действия">
            {ttl.map((t) => (
              <button
                key={t.hours}
                type="button"
                role="radio"
                aria-checked={hours === t.hours}
                onClick={() => setHours(t.hours)}
                className={hours === t.hours ? 'rounded-sm bg-canvas px-2.5 py-1.5 sm:py-1 font-medium shadow-hair' : 'px-2.5 py-1.5 sm:py-1 text-muted hover:text-ink'}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <button className="btn-primary" disabled={!canSend}>
          <Lock className="size-4" /> {send.isPending ? 'Шифруем…' : 'Зашифровать и передать'}
        </button>
      </div>

      {send.error && (
        <p role="alert" className="mt-4 text-sm text-overdue">
          Не передано: {errorMessage(send.error)}
        </p>
      )}
      {sent && (
        <p className="mt-4 flex items-center gap-2 border-t border-line pt-4 text-sm">
          <ShieldCheck className="size-4 text-done" />
          Передано: {sent.to.name} получит уведомление. Когда откроет, статус сменится на «Открыт».
        </p>
      )}
    </form>
  );
}

/** The secret cannot be shown again (opened, expired, or it did not decrypt): ask the sender for a new one. */
function Rerequest({ secret }: { secret: Secret }) {
  const ask = useRerequestSecret();
  if (ask.isSuccess)
    return (
      <p className="mt-3 text-sm" role="status">
        Запрос отправлен: {secret.from.name} получил уведомление и передаст доступ заново.
      </p>
    );
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <button className="btn-secondary" disabled={ask.isPending} onClick={() => ask.mutate(secret.id)}>
        {ask.isPending ? 'Отправляем…' : 'Запросить заново'}
      </button>
      {ask.error && (
        <span role="alert" className="text-sm text-overdue">
          {errorMessage(ask.error)}
        </span>
      )}
    </div>
  );
}

/** Not the BFF (it explains itself in Russian): the browser could not decrypt what the server returned. */
const openFailure = (e: unknown) =>
  e instanceof ApiError || (e instanceof Error && /[а-яё]/i.test(e.message)) ? errorMessage(e instanceof ApiError ? e : new ApiError(0, (e as Error).message)) : 'Не удалось расшифровать значение.';

function Reveal({ secret, value, onOpened }: { secret: Secret; value?: string; onOpened: (v: string) => void }) {
  const open = useOpenSecret();
  const [copied, setCopied] = useState(false);

  if (value !== undefined)
    return (
      <div className="mt-3 rounded bg-sunken p-3">
        <pre className="whitespace-pre-wrap break-all font-mono text-sm">{value}</pre>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-muted">Сохраните сейчас: после ухода со страницы значение не откроется повторно.</span>
          <button
            className="btn-secondary"
            onClick={async () => {
              await navigator.clipboard.writeText(value).catch(() => undefined);
              setCopied(true);
            }}
          >
            <Copy className="size-4" /> {copied ? 'Скопировано' : 'Скопировать'}
          </button>
        </div>
      </div>
    );

  return (
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <button className="btn-primary" disabled={open.isPending} onClick={() => open.mutate(secret.id, { onSuccess: onOpened })}>
        <Eye className="size-4" /> {open.isPending ? 'Расшифровываем…' : 'Открыть один раз'}
      </button>
      {open.error && (
        <span role="alert" className="text-sm text-overdue">
          {openFailure(open.error)}
        </span>
      )}
      {open.error && (
        <div className="basis-full">
          <p className="text-sm text-muted">Значение на сервере уже стёрто, поэтому открыть его повторно нельзя. Попросите передать доступ заново.</p>
          <Rerequest secret={secret} />
        </div>
      )}
    </div>
  );
}

/** What the studio asked for in the launch or later: open «access» items across the client's projects. */
function Requests() {
  const { data: actions } = useActions();
  const { data: projects } = useProjects();
  const [, setParams] = useSearchParams();
  const asked = (actions ?? []).flatMap((a) => (a.kind === 'onboarding' && a.item.kind === 'access' && !a.item.done ? [a.item] : []));
  if (!asked.length) return null;
  return (
    <section className="mb-8" aria-label="Студия просит передать">
      <h2 className="mb-2 text-sm font-semibold">Студия просит передать</h2>
      <ul className="divide-y divide-line border-y border-line">
        {asked.map((i) => (
          <li key={i.id} className="flex flex-wrap items-center gap-3 py-3">
            <span className="min-w-0 flex-1">
              <span className="block font-medium">{i.title}</span>
              <span className="block text-sm text-muted">
                {[projects?.find((p) => p.id === i.projectId)?.title, i.hint].filter(Boolean).join(', ')}
              </span>
            </span>
            <button className="btn-secondary" onClick={() => setParams({ project: i.projectId, title: i.title, onboarding: i.id, ...(i.toId ? { to: i.toId } : {}) })}>
              Передать
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function Access() {
  const { data: ws } = useWorkspace();
  const secretsQuery = useSecrets();
  const secrets = secretsQuery.data;
  const { data: projects } = useProjects();
  const revoke = useRevokeSecret();
  const [tab, setTab] = useState<'in' | 'out'>('in');
  // The plaintext survives the refetch that flips the status to "opened", until the user leaves the page.
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const incoming = secrets?.filter((s) => s.to.id === ws?.me.id) ?? [];
  const outgoing = secrets?.filter((s) => s.from.id === ws?.me.id) ?? [];
  const list = tab === 'in' ? incoming : outgoing;

  return (
    <>
      <PageHeader title="Доступы" description="Пароли и ключи передаются адресно: конкретному человеку, с одноразовым открытием и сроком. Кто и когда открыл — видно здесь." />
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_380px]">
        <div className="min-w-0">
          <Requests />
          <Tabs
            id="access"
            label="Доступы"
            value={tab}
            onChange={setTab}
            items={[
              { value: 'in', label: 'Для вас', count: incoming.length },
              { value: 'out', label: 'Вы передали', count: outgoing.length },
            ]}
          />
          <div {...tabPanel('access', tab)} className="focus:outline-none">
          {!secrets ? (
            <QueryPending query={secretsQuery} />
          ) : list.length === 0 ? (
            <Empty title={tab === 'in' ? 'Для вас ничего нет' : 'Вы ещё ничего не передавали'}>
              {tab === 'in' ? 'Когда студия передаст вам доступ, он появится здесь.' : 'Передайте доступ через форму — его откроет только выбранный человек.'}
            </Empty>
          ) : (
            <ul className="divide-y divide-line border-y border-line">
              {list.map((s) => {
                const st = secretStatus[s.status];
                const other = tab === 'in' ? s.from : s.to;
                return (
                  <li key={s.id} className="py-4">
                    <div className="flex items-start gap-3">
                      <Avatar person={other} size="sm" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="font-medium">{s.title}</span>
                          <Status tone={st.tone}>{st.label}</Status>
                        </div>
                        <div className="mt-0.5 text-sm text-muted">
                          {tab === 'in' ? `от ${other.name}` : `для ${other.name}`}
                          {s.projectId && `, ${projects?.find((p) => p.id === s.projectId)?.title ?? ''}`}, {formatWhen(s.createdAt)}
                          {s.status === 'opened' && s.openedAt && `. Открыт ${formatWhen(s.openedAt)}`}
                          {s.status === 'waiting' && `. Действует до ${formatWhen(s.expiresAt)}`}
                        </div>
                        {tab === 'in' && (s.status === 'waiting' || revealed[s.id] !== undefined) && (
                          <Reveal secret={s} value={revealed[s.id]} onOpened={(v) => setRevealed((r) => ({ ...r, [s.id]: v }))} />
                        )}
                        {tab === 'in' && (s.status === 'opened' || s.status === 'expired') && revealed[s.id] === undefined && <Rerequest secret={s} />}
                      </div>
                      {tab === 'out' && s.status === 'waiting' && (
                        <ConfirmButton label="Отозвать" className="btn-ghost" title="Отозвать доступ?" confirmLabel="Отозвать" quiet onConfirm={() => revoke.mutate(s.id)}>
  Доступ «{s.title}» перестанет открываться. Если получатель его уже открыл, значения у себя нужно сменить.
</ConfirmButton>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          </div>
        </div>
        <div className="lg:sticky lg:top-10 lg:self-start">
          <SendForm />
        </div>
      </div>
    </>
  );
}
